import React from 'react';
import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import {
    App,
    Button,
    Flex,
    Form,
    FormProps,
    Input,
    Popconfirm,
    Table,
    TableProps,
    Image,
    Typography,
    Space,
    Modal,
    Select,
    AutoComplete,
    Spin,
    Switch,
    Tabs
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    SaveOutlined
} from '@ant-design/icons';
import { useEffect, useState } from "react";
import { useStore } from "../../stores/root-store-context";
import ReactQuill from "react-quill";
import { AssignUserProject, Project } from "../../stores/ProjectsStore";
import { TextSort } from "../../helpers/SorterHelper";
import CustomFieldsAdmin from '../../components/elements/KanbanBoard/CustomFieldSettings/CustomFieldSettings';
import AnonymusAccess from './AnonymusAccess';
import { Roles } from '../../enums/Roles';
import { Employee } from '../../models/Employee';
import { canAccessProject } from '../../helpers/ProjectAccessHelper';

const { Option } = Select;
const { TabPane } = Tabs;
const { Paragraph, Title, Text } = Typography;

const ProjectSettings = observer(() => {
    const [form] = Form.useForm();
    const [assignForm] = Form.useForm();
    const { message } = App.useApp();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { projectSlug } = useParams();               // Updated to use projectSlug instead of id
    const { applicationStore, projectsStore, projectDocumentationStore, employeesStore, kanbanTaskStore } = useStore();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("1");
    const [project, setProject] = useState<Project | undefined>();
    const [projectId, setProjectId] = useState<string | undefined>();

    const showModal = () => setIsModalOpen(true);
    const hideModal = () => {
        setIsModalOpen(false);
        assignForm.resetFields();
    };

    const updatePage = async (forceLoad: boolean = false) => {
        if (projectSlug) {
            try {
                const project = await projectsStore.getBySlug(projectSlug, forceLoad);
                if (project?.developers) {
                    const devNamesWithRoles = project.developers.map(dev => ({
                        id: dev.id ?? -1,
                        name: `${dev.firstName} ${dev.lastName}`,
                        role: dev.role?.title ?? 'No role'
                    }));
                }

                if (project) {
                    if (!canAccessProject(project, applicationStore.user, "full")) {
                        message.error(t("You don`t have permission"));
                        navigate(`/${applicationStore.company?.Tenant}/projects/${projectSlug}`);
                        return;
                    }

                    setProject(project);
                    setProjectId(project.id.toString());
                    form.setFieldsValue(project);

                    await employeesStore.getUsers();
                    if (kanbanTaskStore && typeof kanbanTaskStore.getProjectCustomFields === 'function') {
                        await kanbanTaskStore.getProjectCustomFields(project.id.toString());
                    } else {
                        console.warn('getProjectCustomFields is not available in kanbanTaskStore');
                    }
                } else {
                    message.error(`Project not found: ${projectSlug}`);
                    navigate(`/${applicationStore.company?.Tenant}/projects`);
                }
            } catch (error: any) {
                message.error(error.message);
                navigate(`/${applicationStore.company?.Tenant}/projects`);
            }
        }
    };

    const onSave = async () => {
        message.open({ type: 'loading', content: 'Saving...', key: 'Saving' });
        const data = form.getFieldsValue();

        try {
            const result = await projectsStore.editProject(data);

            message.destroy("Saving");
            message.open({ type: 'success', content: 'Saved', key: 'Saved' });

            // if the name has changed, update the list and navigate
            if (data.title && data.title !== project?.title && projectId) {
                // wait for the list to update
                await projectsStore.getAll(true);

                // get a new slug
                const newSlug = projectsStore.getProjectSlug(projectId.toString());
                if (newSlug) {
                    const currentPath = window.location.pathname;
                    const newPath = currentPath.replace(projectSlug!, newSlug);

                    navigate(newPath, { replace: true });
                }
            }
        } catch (error: any) {
            message.destroy("Saving");
            message.error(`Error: ${error.message}`);
        }
    };

    const onChangRole = async (userId: string, roleId: string) => {
        if (!projectId) return message.error('Project ID not found');

        message.open({ type: 'loading', content: 'Saving...', key: 'Saving' });

        const data: AssignUserProject = { projectId, userId, roleId };

        projectsStore.editAssignUser(data)
            .then(() => {
                message.destroy("Saving");
                message.open({ type: 'success', content: 'Saved', key: 'Saved' });
            })
            .catch(error => {
                message.destroy("Saving");
                message.error(`Error: ${error.message}`);
            });
    };

    const onAssignUser: FormProps<AssignUserProject>['onFinish'] = async () => {
        const data = assignForm.getFieldsValue();
        const userId = data.userId;
        if (!projectId) return message.error('Project ID not found');

        message.open({ type: 'loading', content: 'Saving...', key: 'Saving' });

        if (userId) {
            const assignData: AssignUserProject = {
                projectId,
                userId,
                roleId: data.role.key
            };

            projectsStore.assignNewUser(assignData)
                .finally(() => {
                    hideModal();
                    projectsStore.project.developers = projectsStore.project.developers.map(dev => {
                        if (dev.id === userId) {
                            dev.role = { id: data.role.key, title: data.role.label };
                        }
                        return dev;
                    });
                    message.destroy("Saving");
                    message.open({ type: 'success', content: 'Saved', key: 'Saved' });
                });
        }
    };

    const optionsRole = [
        // { key: Roles.Owner,   value: Roles.Owner,   label: 'Owner',  description: 'All access' },
        { key: Roles.Admin, value: Roles.Admin, label: 'Admin', description: 'All access' },
        { key: Roles.User, value: Roles.User, label: 'User', description: 'Editing, and inviting users' },
        { key: Roles.Viewer, value: Roles.Viewer, label: 'Viewer', description: 'Only viewing' },
    ];

    const columns: TableProps<any>['columns'] = [
        {
            title: '',
            dataIndex: 'photoId',
            key: 'photo',
            render: (photo) => (
                <Image
                    width={40}
                    height={40}
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                    src={photo
                        ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${photo}`
                        : `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`
                    }
                />
            ),
        },
        {
            title: 'Name',
            dataIndex: 'firstName',
            key: 'firstName',
            render: (_, record) => `${record.firstName} ${record.lastName}`,
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.lastName)
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (_, record) => <div>{record.email}</div>,
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.lastName)
        },
        {
            title: 'Roles',
            dataIndex: ['role'],
            key: 'Roles',
            width: 300,
            render: (_, record) => {
                return (
                    <Select
                        labelInValue
                        defaultValue={optionsRole.find(option => option.key === record.role?.id)}
                        style={{ width: 120, height: 80 }}
                        onChange={(value: any) => onChangRole(record.id, value.key)}
                        optionLabelProp="label"
                        options={optionsRole.map(option => ({
                            key: option.value,
                            value: option.label,
                            label: (
                                <div>
                                    <div>{option.label}</div>
                                    <div style={{ fontSize: '12px', color: '#888' }}>
                                        {option.description}
                                    </div>
                                </div>
                            )
                        }))}
                    />);
            },
        },
        {
            title: '',
            dataIndex: 'operation',
            render: (_: any, record: any) => (
                <Flex justify="space-between">
                    <Button
                        type="primary"
                        shape="circle"
                        onClick={() =>
                            navigate(`/${applicationStore.company.Tenant}/employees/${record.id}`)
                        }
                        icon={<EyeOutlined />}
                    />
                    <Popconfirm
                        title="Delete the user"
                        description="Are you sure to delete this user?"
                        okText="Yes"
                        cancelText="No"
                        onConfirm={() => {
                            if (!projectId) return message.error('Project ID not found');
                            projectsStore.setLoading(true);
                            projectsStore.deleteAssignUser(record.id, projectId)
                                .then(() => {
                                    updatePage(true);
                                    projectsStore.project.developers = projectsStore.project.developers.filter(dev => dev.id !== record.id);
                                })
                                .catch(error => message.error(`Error: ${error.message}`))
                                .finally(() => projectsStore.setLoading(false));
                        }}
                    >
                        <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Flex>
            ),
        }
    ];

    useEffect(() => {
        updatePage();
    }, [projectSlug]);

    return (
        <Layout headerStyle={1} footerStyle={2}>
            <div className="container" style={{ margin: "20px auto" }}>
                {/* ==== TOP TOOLBAR WITH BACK BUTTON & PROJECT-ID ==== */}
                <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
                    <Button
                        type="primary"
                        style={{ borderRadius: 0 }}
                        onClick={() =>
                            navigate(`/${applicationStore.company?.Tenant}/projects/${projectSlug}`)
                        }
                    >
                        {t("Back")}
                    </Button>

                    {projectId && (
                        <Space>
                            <Text strong>{t("Project ID")}:</Text>
                            <Text copyable code>{projectId}</Text>
                        </Space>
                    )}
                </Flex>

                <Spin spinning={projectsStore.isLoading}>
                    <Tabs defaultActiveKey="1" activeKey={activeTab} onChange={setActiveTab}>
                        {/* ========== GENERAL TAB ========== */}
                        <TabPane tab={t("General")} key="1">
                            <Form form={form} layout="vertical">
                                <Form.Item<Project> name="id" hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item<Project> name="startDate" hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item<Project>
                                    label={t("Title")}
                                    name="title"
                                    style={{ width: "100%" }}
                                    rules={[{ required: true }]}
                                >
                                    <Input placeholder={t("Title")} onBlur={onSave} />
                                </Form.Item>
                                <Form.Item<Project>
                                    label={t("Description")}
                                    name="description"
                                    style={{ width: "100%", marginBottom: 60 }}
                                    rules={[{ required: true }]}
                                >
                                    <ReactQuill theme="snow" style={{ height: 300 }} onBlur={onSave} />
                                </Form.Item>
                                <Form.Item<Project>
                                    label={t("IsPublishProject")}
                                    name="isPublish"
                                    style={{ width: "100%" }}
                                >
                                    <Switch onChange={onSave} />
                                </Form.Item>
                            </Form>
                        </TabPane>

                        {/* ========== USERS TAB ========== */}
                        <TabPane tab={t("Users")} key="2">
                            <Flex gap={10} justify="space-between" style={{ marginBottom: 20 }}>
                                <Title level={4}>{t("Users")}</Title>
                                <Button
                                    type="primary"
                                    style={{ borderRadius: 0 }}
                                    onClick={showModal}
                                    icon={<PlusOutlined />}
                                >
                                    {t("AddUser")}
                                </Button>
                            </Flex>

                            <Table
                                columns={columns}
                                dataSource={projectsStore.project?.members?.map((el, index) => ({
                                    ...el,
                                    key: index
                                }))}
                            />
                        </TabPane>

                        {/* ========== CUSTOM FIELDS TAB ========== */}
                        <TabPane tab={t("CustomFields")} key="3">
                            {projectId && <CustomFieldsAdmin projectId={projectId} />}
                        </TabPane>
                        {/* ========== ANONYMUS ACCESS TAB ========== */}
                        <TabPane tab={t("Anonymus Access")} key="4">
                            <AnonymusAccess />
                        </TabPane>
                    </Tabs>
                </Spin>
            </div>

            {/* ===== ADD-USER MODAL ===== */}
            <Modal
                title={t("AddUser")}
                open={isModalOpen}
                footer={null}
                onCancel={hideModal}
                width={900}
                style={{ padding: 10 }}
            >
                <Form
                    form={assignForm}
                    onFinish={onAssignUser}
                    initialValues={{
                        projectId,
                        creatorId: applicationStore.user.id
                    }}
                    layout="vertical"
                >
                    <Form.Item<any> name="projectId" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item<any> name="email" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item<any> name="userId" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item<any>
                        label={t("User")}
                        name="user"
                        style={{ width: "100%" }}
                        rules={[{ required: true }]}
                    >
                        <AutoComplete
                            style={{ height: 40 }}
                            placeholder={t('User')}
                            options={employeesStore.users
                                .slice()
                                .sort((a, b) => a.firstName.localeCompare(b.firstName, 'uk'))
                                .map((el, index) => ({
                                    key: index,
                                    value: `${el.firstName} ${el.lastName}`,
                                    data: el,
                                    label: (
                                        <Flex gap={5}>
                                            <Image
                                                width={40}
                                                height={40}
                                                preview={false}
                                                style={{
                                                    borderRadius: "50%",
                                                    objectFit: "cover"
                                                }}
                                                src={el.photoId
                                                    ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${el.photoId}`
                                                    : `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`
                                                }
                                            />
                                            <div>
                                                <div>{el.firstName} {el.lastName}</div>
                                                <div>{el.email}</div>
                                            </div>
                                        </Flex>
                                    )
                                }))}
                            onChange={(_, option: any) =>
                                assignForm.setFieldValue("userId", option.data?.id)
                            }
                        />
                    </Form.Item>
                    <Form.Item<any>
                        label={t("Role")}
                        name="role"
                        style={{ width: "100%", marginBottom: 30 }}
                        rules={[{ required: true }]}
                    >
                        <Select
                            labelInValue
                            optionLabelProp="label"
                            style={{ width: '100%' }}
                            options={optionsRole
                                .slice()
                                .sort((a, b) => a.label.localeCompare(b.label, 'uk'))
                                .map(option => ({
                                    key: option.value,
                                    value: option.value,
                                    label: option.label,
                                    children: (
                                        <div>
                                            <div>{option.label}</div>
                                            <div style={{ fontSize: '12px', color: '#888' }}>
                                                {option.description}
                                            </div>
                                        </div>
                                    )
                                }))}
                        />
                    </Form.Item>
                    <Form.Item style={{ margin: 0 }}>
                        <Flex justify="end">
                            <Button
                                type="primary"
                                className="theme-btn"
                                htmlType="submit"
                                style={{ height: 65, marginTop: 30 }}
                            >
                                {t("Save")} <SaveOutlined style={{ fontSize: 20, marginLeft: 5 }} />
                            </Button>
                        </Flex>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
});

export default ProjectSettings;
